import React from 'react';
import { useTranslation } from '../Context/TranslationContext';
import { motion } from 'framer-motion';
import { 
  Target, Compass, Heart, Book, 
  Star, Award, Flag, Users, 
  Clock, MapPin, Globe, GraduationCap 
} from 'lucide-react';

export default function WhoWeAre() {
  const { isTamil } = useTranslation();

  const translations = {
    en: {
      title: "Who We Are",
      subtitle: "The Bharat Scouts and Guides - Tamil Nadu",
      intro: "The Bharat Scouts and Guides is a voluntary, non-political, educational movement for young people, open to all without distinction of origin, race or creed.",
      vision: {
        title: "Vision & Mission",
        vision: "To be the country's leading youth movement, enabling young people to develop their full potential as responsible citizens.",
        mission: "To contribute to the education of young people through a value system based on the Scout Promise and Law.",
        values: [
          {
            title: "Character Building",
            desc: "Developing strong moral values and leadership qualities"
          },
          {
            title: "Citizenship",
            desc: "Creating responsible and active citizens of India"
          },
          {
            title: "Service",
            desc: "Promoting the spirit of selfless service to society"
          }
        ]
      },
      scoutPromise: {
        title: "Scout Promise",
        text: [
          "On my honour, I promise that I will do my best",
          "To do my duty to God* and my Country",
          "To help other people at all times",
          "And to obey the Scout Law"
        ],
        note: "* The word 'Dharma' can be substituted for the word 'God' if so desired"
      },
      guidePromise: {
        title: "Guide Promise",
        text: [
          "On my honour, I promise that I will do my best:",
          "To do my duty to God* and my Country",
          "To help other people, and",
          "To obey the Guide Law"
        ],
        note: "* The word 'Dharma' can be substituted for the word 'God' if so desired"
      },
      laws: {
        scoutTitle: "The Scout Law",
        guideTitle: "The Guide Law",
        laws: [
          "Trustworthy and truthful at all times",
          "Loyal to the country, parents, leaders, and subordinates",
          "A friend to all and a brother/sister to every other Scout/Guide",
          "Courteous and respectful to all",
          "A friend to animals and protector of nature",
          "Disciplined and helps protect public property",
          "Courageous and cheerful in all difficulties",
          "Thrifty and respects others' property",
          "Pure in thought, word, and deed"
        ]
      },
      activities: {
        title: "Core Activities",
        items: [
          {
            title: "Camping",
            desc: "Learning outdoor skills and self-reliance"
          },
          {
            title: "Community Service",
            desc: "Engaging in social welfare and community development"
          },
          {
            title: "Skill Development",
            desc: "Acquiring practical skills and badges"
          },
          {
            title: "Adventure",
            desc: "Participating in hiking, trekking and exploration"
          },
          {
            title: "Cultural Exchange",
            desc: "Attending jamborees and international events"
          },
          {
            title: "Environmental Protection",
            desc: "Contributing to nature conservation"
          }
        ]
      },
      stats: [
        { number: "2.5M+", label: "Active Members" },
        { number: "38", label: "District Units" },
        { number: "50K+", label: "Adult Leaders" },
        { number: "1000+", label: "Annual Events" }
      ]
    },
    ta: {
      title: "நாங்கள் யார்",
      subtitle: "பாரத சாரணர் மற்றும் சாரணியர் - தமிழ்நாடு",
      intro: "பாரத சாரணர் மற்றும் சாரணியர் இயக்கம் என்பது தன்னார்வ, அரசியல் சாரா, கல்வி இயக்கமாகும், இது பிறப்பு, இனம் அல்லது மதம் பாகுபாடின்றி அனைவருக்கும் திறந்திருக்கிறது.",
      vision: {
        title: "நோக்கம் & பணி",
        vision: "இளைஞர்களை பொறுப்புள்ள குடிமக்களாக வளர்க்கும் முன்னணி இளைஞர் இயக்கமாக திகழ்வது.",
        mission: "சாரணர் உறுதிமொழி மற்றும் விதிகளின் அடிப்படையிலான மதிப்பு முறை மூலம் இளைஞர்களின் கல்விக்கு பங்களிப்பது.",
        values: [
          {
            title: "குண உருவாக்கம்",
            desc: "வலுவான அறநெறி மதிப்புகள் மற்றும் தலைமைப் பண்புகளை வளர்த்தல்"
          },
          {
            title: "குடியுரிமை",
            desc: "இந்தியாவின் பொறுப்புள்ள மற்றும் செயலில் உள்ள குடிமக்களை உருவாக்குதல்"
          },
          {
            title: "சேவை",
            desc: "சமூகத்திற்கான சுயநலமற்ற சேவை உணர்வை ஊக்குவித்தல்"
          }
        ]
      },
      scoutPromise: {
        title: "சாரணர் உறுதிமொழி",
        text: [
          "என் கௌரவத்தின் மீது, நான் என் சிறந்த முயற்சியை செய்வேன் என உறுதியளிக்கிறேன்",
          "கடவுளுக்கும்* என் நாட்டிற்கும் என் கடமையை செய்ய",
          "எப்போதும் பிறருக்கு உதவ",
          "சாரணர் சட்டத்திற்கு கீழ்ப்படிய"
        ],
        note: "* 'கடவுள்' என்ற சொல்லுக்கு பதிலாக 'தர்மம்' என்ற சொல்லை பயன்படுத்தலாம்"
      },
      guidePromise: {
        title: "சாரணியர் உறுதிமொழி",
        text: [
          "என் கௌரவத்தின் மீது, நான் என் சிறந்த முயற்சியை செய்வேன் என உறுதியளிக்கிறேன்:",
          "கடவுளுக்கும்* என் நாட்டிற்கும் என் கடமையை செய்ய",
          "பிறருக்கு உதவ",
          "சாரணியர் சட்டத்திற்கு கீழ்ப்படிய"
        ],
        note: "* 'கடவுள்' என்ற சொல்லுக்கு பதிலாக 'தர்மம்' என்ற சொல்லை பயன்படுத்தலாம்"
      },
      laws: {
        scoutTitle: "சாரணர் சட்டம்",
        guideTitle: "சாரணியர் சட்டம்",
        laws: [
          "எப்போதும் நம்பிக்கைக்குரியவர் மற்றும் உண்மையானவர்",
          "நாடு, பெற்றோர், தலைவர்கள் மற்றும் கீழ்நிலையினருக்கு விசுவாசமானவர்",
          "அனைவருக்கும் நண்பர் மற்றும் ஒவ்வொரு சாரணர்/சாரணியருக்கும் சகோதரர்/சகோதரி",
          "அனைவரிடமும் மரியாதையுடன் நடந்துகொள்பவர்",
          "விலங்குகளுக்கு நண்பர் மற்றும் இயற்கையின் பாதுகாவலர்",
          "ஒழுக்கமானவர் மற்றும் பொது சொத்துக்களை பாதுகாப்பவர்",
          "துணிச்சலானவர் மற்றும் அனைத்து சிரமங்களிலும் மகிழ்ச்சியானவர்",
          "சிக்கனமானவர் மற்றும் மற்றவர்களின் சொத்துக்களை மதிப்பவர்",
          "எண்ணம், சொல் மற்றும் செயலில் தூய்மையானவர்"
        ]
      },
      activities: {
        title: "முக்கிய செயல்பாடுகள்",
        items: [
          {
            title: "முகாமிடல்",
            desc: "வெளிப்புற திறன்கள் மற்றும் சுய சார்பு கற்றல்"
          },
          {
            title: "சமூக சேவை",
            desc: "சமூக நலன் மற்றும் சமூக மேம்பாட்டில் ஈடுபடுதல்"
          },
          {
            title: "திறன் மேம்பாடு",
            desc: "நடைமுறை திறன்கள் மற்றும் குறிகள் பெறுதல்"
          },
          {
            title: "சாகசம்",
            desc: "நடைபயணம், மலையேற்றம் மற்றும் ஆய்வுப் பயணங்களில் பங்கேற்றல்"
          },
          {
            title: "கலாச்சார பரிமாற்றம்",
            desc: "ஜாம்போரி மற்றும் சர்வதேச நிகழ்வுகளில் பங்கேற்றல்"
          },
          {
            title: "சுற்றுச்சூழல் பாதுகாப்பு",
            desc: "இயற்கை பாதுகாப்பிற்கு பங்களித்தல்"
          }
        ]
      },
      stats: [
        { number: "2.5M+", label: "செயலில் உள்ள உறுப்பினர்கள்" },
        { number: "38", label: "மாவட்ட அலகுகள்" },
        { number: "50K+", label: "பெரியவர் தலைவர்கள்" },
        { number: "1000+", label: "வருடாந்திர நிகழ்வுகள்" }
      ]
    }
  };

  const t = translations[isTamil ? 'ta' : 'en'];

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-12 mt-16">
      <div className="max-w-7xl mx-auto px-4">
        {/* Introduction Section */}
        <div className="text-center mb-16">
       
          <h2 className={`text-2xl font-medium text-gray-700 mb-6 ${isTamil ? 'text-xl' : ''}`}>
            {t.subtitle}
          </h2>
          <p className={`text-lg text-gray-600 max-w-3xl mx-auto ${isTamil ? 'text-base' : ''}`}>
            {t.intro}
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
          {t.stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-white rounded-lg p-6 text-center shadow-sm"
            >
              <div className="text-3xl font-bold text-blue-600 mb-2">
                {stat.number}
              </div>
              <div className={`text-gray-600 ${isTamil ? 'text-sm' : ''}`}>
                {stat.label}
              </div>
            </motion.div>
          ))}
        </div>

       {/* Vision & Mission */}
       <div className="mb-16">
          <h2 className={`text-3xl font-bold text-center mb-8 ${isTamil ? 'text-2xl' : ''}`}>
            {t.vision.title}
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-lg p-8 shadow-sm">
              <div className="flex items-center mb-4">
                <Target className="w-8 h-8 text-blue-600 mr-3" />
                <h3 className={`text-xl font-semibold ${isTamil ? 'text-lg' : ''}`}>Vision</h3>
              </div>
              <p className="text-gray-600">{t.vision.vision}</p>
            </div>
            <div className="bg-white rounded-lg p-8 shadow-sm">
              <div className="flex items-center mb-4">
                <Compass className="w-8 h-8 text-blue-600 mr-3" />
                <h3 className={`text-xl font-semibold ${isTamil ? 'text-lg' : ''}`}>Mission</h3>
              </div>
              <p className="text-gray-600">{t.vision.mission}</p>
            </div>
          </div>
          
          {/* Core Values */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
            {t.vision.values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-lg p-6 shadow-sm"
              >
                <h3 className={`text-lg font-semibold mb-3 text-gray-900 ${isTamil ? 'text-base' : ''}`}>
                  {value.title}
                </h3>
                <p className={`text-gray-600 ${isTamil ? 'text-sm' : ''}`}>
                  {value.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Scout and Guide Promise */}
        <div className="mb-16">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Scout Promise */}
            <div className="bg-white rounded-lg p-8 shadow-sm">
              <div className="flex items-center mb-6">
                <Flag className="w-8 h-8 text-blue-600 mr-3" />
                <h3 className={`text-xl font-semibold ${isTamil ? 'text-lg' : ''}`}>
                  {t.scoutPromise.title}
                </h3>
              </div>
              <div className="space-y-3">
                {t.scoutPromise.text.map((line, index) => (
                  <p key={index} className="text-gray-600">{line}</p>
                ))}
              </div>
              <p className="mt-4 text-sm text-gray-500 italic">{t.scoutPromise.note}</p>
            </div>

            {/* Guide Promise */}
            <div className="bg-white rounded-lg p-8 shadow-sm">
              <div className="flex items-center mb-6">
                <Flag className="w-8 h-8 text-blue-600 mr-3" />
                <h3 className={`text-xl font-semibold ${isTamil ? 'text-lg' : ''}`}>
                  {t.guidePromise.title}
                </h3>
              </div>
              <div className="space-y-3">
                {t.guidePromise.text.map((line, index) => (
                  <p key={index} className="text-gray-600">{line}</p>
                ))}
              </div>
              <p className="mt-4 text-sm text-gray-500 italic">{t.guidePromise.note}</p>
            </div>
          </div>
        </div>

        {/* Scout and Guide Laws */}
        <div className="mb-16">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Scout Laws */}
            <div className="bg-white rounded-lg p-8 shadow-sm">
              <div className="flex items-center mb-6">
                <Book className="w-8 h-8 text-blue-600 mr-3" />
                <h3 className={`text-xl font-semibold ${isTamil ? 'text-lg' : ''}`}>
                  {t.laws.scoutTitle}
                </h3>
              </div>
              <ol className="list-decimal list-inside space-y-2">
                {t.laws.laws.map((law, index) => (
                  <li key={index} className="text-gray-600">{law}</li>
                ))}
              </ol>
            </div>

            {/* Guide Laws */}
            <div className="bg-white rounded-lg p-8 shadow-sm">
              <div className="flex items-center mb-6">
                <Book className="w-8 h-8 text-blue-600 mr-3" />
                <h3 className={`text-xl font-semibold ${isTamil ? 'text-lg' : ''}`}>
                  {t.laws.guideTitle}
                </h3>
              </div>
              <ol className="list-decimal list-inside space-y-2">
                {t.laws.laws.map((law, index) => (
                  <li key={index} className="text-gray-600">{law}</li>
                ))}
              </ol>
            </div>
          </div>
        </div>

        {/* Core Activities */}
        <div className="mb-16">
          <h2 className={`text-3xl font-bold text-center mb-8 ${isTamil ? 'text-2xl' : ''}`}>
            {t.activities.title}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {t.activities.items.map((activity, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-lg p-6 shadow-sm"
              >
                <h3 className={`text-lg font-semibold mb-3 text-gray-900 ${isTamil ? 'text-base' : ''}`}>
                  {activity.title}
                </h3>
                <p className={`text-gray-600 ${isTamil ? 'text-sm' : ''}`}>
                  {activity.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Core Values */}
        <div>
          <h2 className={`text-3xl font-bold text-center mb-8 ${isTamil ? 'text-2xl' : ''}`}>
            Our Core Values
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {t.vision.values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-lg p-6 shadow-sm"
              >
                <h3 className={`text-lg font-semibold mb-3 text-gray-900 ${isTamil ? 'text-base' : ''}`}>
                  {value.title}
                </h3>
                <p className={`text-gray-600 ${isTamil ? 'text-sm' : ''}`}>
                  {value.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
